import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../../services/apiService';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const emptyProduct = {
  productId: undefined,
  productName: '',
  productNameTamil: '',
  productNameEnglish: '',
  productPlace: '',
  actualPrice: '',
  sellingPrice: '',
  stock: '',
};

const AddProductDialog = ({ open, onClose, onSaveSuccess, initialData = null }) => {
  const [form, setForm] = useState(emptyProduct);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saveError, setSaveError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({ ...emptyProduct, ...initialData });
      } else {
        setForm(emptyProduct);
      }
      setSubmitAttempted(false);
      setSaveError('');
    }
  }, [open, initialData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSaveError('');
  };

  const transliterateToTamil = (text) => {
    if (!text) return '';

    const vowels = {
      'a': 'அ', 'aa': 'ஆ', 'i': 'இ', 'ii': 'ஈ', 'u': 'உ', 'uu': 'ஊ',
      'e': 'எ', 'ee': 'ஏ', 'ai': 'ஐ', 'o': 'ஒ', 'oo': 'ஓ', 'au': 'ஔ'
    };

    const signs = {
      'a': '', 'aa': 'ா', 'i': 'ி', 'ii': 'ீ', 'u': 'ு', 'uu': 'ூ',
      'e': 'ெ', 'ee': 'ே', 'ai': 'ை', 'o': 'ொ', 'oo': 'ோ', 'au': 'ௌ'
    };

    const consonants = {
      'k': 'க்', 'g': 'க்', 'ng': 'ங்', 'c': 'ச்', 'ch': 'ச்', 'j': 'ஜ்', 'gn': 'ஞ்', 'nj': 'ஞ்',
      't': 'ட்', 'd': 'ட்', 'N': 'ண்', 'th': 'த்', 'dh': 'த்', 'n': 'ந்',
      'p': 'ப்', 'b': 'ப்', 'm': 'ம்',
      'y': 'ய்', 'r': 'ர்', 'l': 'ல்', 'v': 'வ்', 'z': 'ழ்', 'zh': 'ழ்', 'L': 'ள்', 'R': 'ற்',
      'sh': 'ஷ்', 'S': 'ஸ்', 's': 'ஸ்', 'h': 'ஹ்', 'ksh': 'க்ஷ்'
    };

    const overrides = {
      'n': 'ன்', 'na': 'ந', 'naa': 'நா', 'ni': 'நி', 'nii': 'நீ', 'nu': 'நு', 'nuu': 'நூ', 'ne': 'நெ', 'nee': 'நே', 'nai': 'நை', 'no': 'நொ', 'noo': 'நோ', 'nau': 'நௌ',
      'sa': 'ச', 'saa': 'சா', 'si': 'சி', 'sii': 'சீ', 'su': 'சு', 'suu': 'சூ', 'se': 'செ', 'see': 'சே', 'sai': 'சை', 'so': 'சொ', 'soo': 'சோ', 'sau': 'சௌ'
    };

    let result = '';
    let i = 0;
    const len = text.length;
    const consKeys = Object.keys(consonants).sort((a, b) => b.length - a.length);
    const vowKeys = Object.keys(vowels).sort((a, b) => b.length - a.length);

    while (i < len) {
      let match = false;
      const sub = text.substring(i).toLowerCase();

      for (const c of consKeys) {
        if (sub.startsWith(c)) {
          const subAfter = sub.substring(c.length);
          let vMatch = null;
          for (const v of vowKeys) {
            if (subAfter.startsWith(v)) {
              vMatch = v;
              break;
            }
          }

          if (vMatch) {
            const key = c + vMatch;
            if (overrides[key]) {
              result += overrides[key];
            } else {
              const base = consonants[c].replace('்', '');
              result += base + signs[vMatch];
            }
            i += c.length + vMatch.length;
          } else {
            if (overrides[c]) {
              result += overrides[c];
            } else {
              result += consonants[c];
            }
            i += c.length;
          }
          match = true;
          break;
        }
      }

      if (match) continue;

      for (const v of vowKeys) {
        if (sub.startsWith(v)) {
          result += vowels[v];
          i += v.length;
          match = true;
          break;
        }
      }

      if (match) continue;

      result += text[i];
      i++;
    }
    return result;
  };

  const saveProduct = async () => {
    setSaveError('');
    try {
      setSubmitAttempted(true);
      const nameOk = (form.productName || '').trim().length > 0;

      if (!nameOk) {
        return;
      }

      const payload = { ...form };
      let response;

      if (payload.productId) {
        response = await apiService.products.update(payload);
      } else {
        response = await apiService.products.create(payload);
      }

      if (onSaveSuccess) {
        onSaveSuccess(response.data);
      }
      onClose();
    } catch (e) {
      console.error('Save failed', e);
      const resData = e.response?.data;
      const errorMessage = resData?.message || resData?.error || e.message || 'Failed to save product';
      setSaveError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionComponent={Transition}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {form.productId ? 'Edit Product' : 'Add Product'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
          <TextField
            label="Name"
            name="productName"
            value={form.productName}
            onChange={handleFormChange}
            required
            error={submitAttempted && !(form.productName || '').trim()}
            helperText={submitAttempted && !(form.productName || '').trim() ? 'Required' : ''}
            size="small"
          />
          <TextField
            label="Name (Tamil)"
            name="productNameTamil"
            value={form.productNameTamil}
            onChange={handleFormChange}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                if (e.key === ' ') {
                  e.preventDefault();
                  const tn = transliterateToTamil(form.productNameTamil || '');
                  setForm(prev => ({ ...prev, productNameTamil: tn + ' ' }));
                } else {
                  const tn = transliterateToTamil(form.productNameTamil || '');
                  setForm(prev => ({ ...prev, productNameTamil: tn }));
                }
              }
            }}
            size="small"
          />
          <TextField
            label="Name (English)"
            name="productNameEnglish"
            value={form.productNameEnglish}
            onChange={handleFormChange}
            size="small"
          />
          <TextField label="Place" name="productPlace" value={form.productPlace} onChange={handleFormChange} size="small" />
          <TextField label="Actual Price" name="actualPrice" value={form.actualPrice} onChange={handleFormChange} size="small" type="number" />
          <TextField label="Selling Price" name="sellingPrice" value={form.sellingPrice} onChange={handleFormChange} size="small" type="number" />
          <TextField label="Stock" name="stock" value={form.stock} onChange={handleFormChange} size="small" type="number" />
        </Box>
        {saveError && (
          <Box sx={{ mt: 2, p: 1, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
            <Typography color="error" variant="body2" align="center">
              {saveError}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={saveProduct}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductDialog;
